/**
 * EmailPrintDocument — the one thing on paper when a CSR prints an email.
 *
 * Rendered through PrintDocumentRoot only while a print is being prepared,
 * so the page behind the modal, alert bars, order cards, AI banners, tabs
 * and buttons can never leak onto the page. Every message in the thread
 * prints expanded: replies without their quoted history (the oldest message
 * in full), each marked when something was left out.
 *
 * HTML bodies render inline in a shadow root (see utils/printEmailHtml) so
 * they paginate like text. `onReady` fires once every body is in place and
 * its images have loaded, so window.print() never catches a half-rendered
 * page; a timeout caps the wait.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { OrderLineItem, OrderRecord } from '../../types/orders';
import type { PrintableEmailMessage } from '../../utils/emailPrintModel';
import { PRINT_EMAIL_BASE_STYLE, purifyEmailHtmlForPrint } from '../../utils/printEmailHtml';
import PrintDocumentRoot from './PrintDocumentRoot';
import PrintOrderSection from './PrintOrderSection';
import styles from './EmailPrintDocument.module.css';

/** Longest we wait for HTML bodies before printing anyway. */
export const PRINT_READY_TIMEOUT_MS = 4000;

export interface EmailPrintJob {
  messages: PrintableEmailMessage[];
  order?: { order: OrderRecord; lineItems: OrderLineItem[] } | null;
}

interface EmailPrintDocumentProps {
  job: EmailPrintJob;
  onReady: () => void;
}

const PrintHtmlBody: React.FC<{ id: string; html: string; onRendered: (id: string) => void }> = ({
  id,
  html,
  onRendered,
}) => {
  const hostRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let cancelled = false;
    const root = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = PRINT_EMAIL_BASE_STYLE;
    root.replaceChildren(style, purifyEmailHtmlForPrint(html));

    const pending = Array.from(root.querySelectorAll('img')).filter((img) => !img.complete);
    let left = pending.length;
    const done = () => {
      if (!cancelled && --left <= 0) onRendered(id);
    };
    if (left === 0) onRendered(id);
    pending.forEach((img) => {
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
    return () => {
      cancelled = true;
    };
  }, [id, html, onRendered]);

  return <div ref={hostRef} className={styles.htmlBody} data-testid="email-print-html" />;
};

const HeaderRow: React.FC<{ label: string; value: string | null }> = ({ label, value }) =>
  value ? (
    <div className={styles.headerRow}>
      <span className={styles.headerLabel}>{label}</span>
      <span>{value}</span>
    </div>
  ) : null;

const EmailPrintDocument: React.FC<EmailPrintDocumentProps> = ({ job, onReady }) => {
  const htmlIds = job.messages.filter((m) => m.bodyHtml).map((m) => m.id);
  const [renderedIds, setRenderedIds] = useState<ReadonlySet<string>>(() => new Set());
  const firedRef = useRef(false);
  const allRendered = htmlIds.every((id) => renderedIds.has(id));

  const fireReady = useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    onReady();
  }, [onReady]);

  const handleRendered = useCallback((id: string) => {
    setRenderedIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, [setRenderedIds]);

  useEffect(() => {
    if (allRendered) {
      // One frame so everything is laid out before the print snapshot.
      const raf = requestAnimationFrame(fireReady);
      return () => cancelAnimationFrame(raf);
    }
    const timer = window.setTimeout(fireReady, PRINT_READY_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [allRendered, fireReady]);

  const title = job.messages[0]?.subject ?? '(no subject)';
  const count = job.messages.length;

  return (
    <PrintDocumentRoot
      title={title}
      subtitle={count > 1 ? `${count} messages` : null}
      testId="email-print-document"
    >
      {job.messages.map((m) => (
        <section key={m.id} className={styles.message} data-testid="email-print-message">
          <div className={styles.headers}>
            <HeaderRow label="From:" value={m.from || null} />
            <HeaderRow label="Date:" value={m.date} />
            <HeaderRow label="To:" value={m.to} />
            <HeaderRow label="Cc:" value={m.cc} />
            {count > 1 && m.subject && m.subject !== title && (
              <HeaderRow label="Subject:" value={m.subject} />
            )}
            <HeaderRow
              label="Attachments:"
              value={m.attachmentNames.length > 0 ? m.attachmentNames.join(', ') : null}
            />
          </div>
          {m.bodyHtml ? (
            <PrintHtmlBody id={m.id} html={m.bodyHtml} onRendered={handleRendered} />
          ) : (
            <div className={styles.textBody}>{m.bodyText ?? ''}</div>
          )}
          {m.quotedHidden && (
            <div className={styles.quotedNote} data-testid="email-print-quoted-note">
              Earlier messages quoted in this reply are not repeated here.
            </div>
          )}
        </section>
      ))}
      {job.order && (
        <PrintOrderSection order={job.order.order} lineItems={job.order.lineItems} />
      )}
    </PrintDocumentRoot>
  );
};

export default EmailPrintDocument;
